/**
 * Named OpenPanel page-land events for the Events widget
 * (HomepageLanded, BlogPageLanded, …). Overview Sources/UTM still use SDK screen_view.
 * UTM / campaign props are merged on each fire via openpanelEvents.
 */

import { parsePoojaPathname } from './poojaRoutes';
import { tryFireOpenPanelPageLandEvent } from './openpanelEvents';

/** @returns {{ event: string, page_name: string, pooja_slug?: string, booking_id?: string }} */
export function resolveOpenPanelPageLand(pathname = '') {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';

  const pooja = parsePoojaPathname(path);
  if (pooja.slug && pooja.isBook) {
    return { event: 'PoojaBookPageLanded', page_name: 'Pooja Book', pooja_slug: pooja.slug };
  }
  if (pooja.slug) {
    return { event: 'PoojaDetailPageLanded', page_name: 'Pooja Detail', pooja_slug: pooja.slug };
  }

  if (/^\/thank-you\/[^/]+\/[^/]+$/i.test(path)) {
    const parts = path.split('/');
    return {
      event: 'ThankYouPageLanded',
      page_name: 'Thank You',
      pooja_slug: parts[2],
      booking_id: parts[3],
    };
  }
  if (/^\/thank-you\/[^/]+$/i.test(path)) {
    return { event: 'ThankYouPageLanded', page_name: 'Thank You', pooja_slug: path.split('/')[2] };
  }
  if (/^\/order-confirmed\/[^/]+$/i.test(path)) {
    return {
      event: 'OrderConfirmedPageLanded',
      page_name: 'Order Confirmed',
      booking_id: path.split('/')[2],
    };
  }
  if (/^\/invoice\/[^/]+$/i.test(path)) {
    return { event: 'InvoicePageLanded', page_name: 'Invoice', booking_id: path.split('/')[2] };
  }
  if (/^\/feedback\/[^/]+$/i.test(path)) {
    return { event: 'FeedbackPageLanded', page_name: 'Feedback', booking_id: path.split('/')[2] };
  }
  if (/^\/live-chat\/[^/]+$/i.test(path)) {
    return { event: 'LiveChatPageLanded', page_name: 'Live Chat' };
  }
  if (/^\/store\/thank-you\/[^/]+$/i.test(path)) {
    return { event: 'StoreThankYouPageLanded', page_name: 'Store Thank You' };
  }
  if (/^\/blogs?\/[^/]+$/i.test(path)) {
    return { event: 'BlogDetailPageLanded', page_name: 'Blog Detail', blog_slug: path.split('/')[2] };
  }
  if (/^\/live-tv\/[^/]+$/i.test(path)) {
    return { event: 'LiveTvDetailPageLanded', page_name: 'Live TV Detail' };
  }
  if (/^\/daiv-nitya\/grid$/i.test(path)) {
    return { event: 'DaivNityaGridPageLanded', page_name: 'Daiv Nitya Grid' };
  }
  if (/^\/daiv-nitya\/[^/]+$/i.test(path)) {
    return { event: 'DaivNityaDetailPageLanded', page_name: 'Daiv Nitya Detail' };
  }

  const staticPages = {
    '/': { event: 'HomepageLanded', page_name: 'Homepage' },
    '/about': { event: 'AboutPageLanded', page_name: 'About' },
    '/temple': { event: 'TemplePageLanded', page_name: 'Temple' },
    '/astrology': { event: 'AstroPageLanded', page_name: 'Astrology' },
    '/nri': { event: 'NriPageLanded', page_name: 'NRI' },
    '/poojas': { event: 'PoojasPageLanded', page_name: 'Poojas' },
    '/past': { event: 'PoojasPageLanded', page_name: 'Poojas Past' },
    '/upcoming': { event: 'PoojasPageLanded', page_name: 'Poojas Upcoming' },
    '/store': { event: 'StorePageLanded', page_name: 'Store' },
    '/blogs': { event: 'BlogPageLanded', page_name: 'Blogs' },
    '/orders': { event: 'OrdersPageLanded', page_name: 'Orders' },
    '/profile': { event: 'ProfilePageLanded', page_name: 'Profile' },
    '/cart': { event: 'CartPageLanded', page_name: 'Cart' },
    '/contact': { event: 'ContactPageLanded', page_name: 'Contact' },
    '/mobile-app': { event: 'MobileAppPageLanded', page_name: 'Mobile App' },
    '/gallery': { event: 'GalleryPageLanded', page_name: 'Gallery' },
    '/live-tv': { event: 'LiveTvPageLanded', page_name: 'Live TV' },
    '/daiv-nitya': { event: 'DaivNityaPageLanded', page_name: 'Daiv Nitya' },
    '/pay': { event: 'PayPageLanded', page_name: 'Pay' },
    '/login': { event: 'LoginPageLanded', page_name: 'Login' },
    '/refund-policy': { event: 'PolicyPageLanded', page_name: 'Refund Policy' },
    '/terms-and-conditions': { event: 'PolicyPageLanded', page_name: 'Terms and Conditions' },
    '/privacy-policy': { event: 'PolicyPageLanded', page_name: 'Privacy Policy' },
    '/mobile-app/privacy-policy': { event: 'PolicyPageLanded', page_name: 'Mobile App Privacy Policy' },
    '/mobile-app/terms-and-conditions': { event: 'PolicyPageLanded', page_name: 'Mobile App Terms and Conditions' },
    '/cancellation-refund-policy': { event: 'PolicyPageLanded', page_name: 'Cancellation Refund Policy' },
  };

  return staticPages[path] || { event: 'AppPageLanded', page_name: path };
}

/** Fire a named page-land event for the current SPA route. */
export function tryFireOpenPanelPageLand(pathname = '') {
  const resolved = resolveOpenPanelPageLand(pathname);
  if (!resolved?.event) return;
  tryFireOpenPanelPageLandEvent(resolved.event, {
    page_name: resolved.page_name,
    page_path: String(pathname || '/'),
    ...(resolved.pooja_slug ? { pooja_slug: resolved.pooja_slug } : {}),
    ...(resolved.booking_id ? { booking_id: resolved.booking_id } : {}),
    ...(resolved.blog_slug ? { blog_slug: resolved.blog_slug } : {}),
  });
}
